// REQ-P1-06b 見本: テンプレート一覧の契約型付きQueryフック。
// 新規のサーバー状態取得はこの形(契約型クライアント + useQuery)を踏襲する。

import { useQuery } from '@tanstack/react-query';
import { api, type paths } from './client';

type TemplateListResponse = NonNullable<
  paths['/templates']['get']['responses'][200]['content']
>['application/json'];
type TemplateMutationRequest = NonNullable<
  paths['/templates']['post']['requestBody']
>['content']['application/json'];
type TemplatePayload = {
  template?: Record<string, unknown>;
  templates?: TemplateListResponse['templates'];
  [key: string]: unknown;
};

export class ApiStatusError extends Error {
  status: number;

  constructor(status: number) {
    super(`HTTP ${status}`);
    this.name = 'ApiStatusError';
    this.status = status;
  }
}

async function readJsonObject(response: Response): Promise<Record<string, unknown>> {
  try {
    const json = await response.clone().json();
    return json && typeof json === 'object' ? (json as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

async function ensureOkWithPayload(
  result: { error?: unknown; response: Response },
  fallbackMessage: string
): Promise<TemplatePayload> {
  const payload = await readJsonObject(result.response);
  if (result.response.ok) return payload;
  const message =
    (typeof payload.message === 'string' && payload.message) ||
    (typeof payload.error === 'string' && payload.error) ||
    (typeof result.error === 'string' && result.error) ||
    fallbackMessage;
  const error = new ApiStatusError(result.response.status);
  error.message = message;
  throw error;
}

export async function listTemplates(type: string, options: { signal?: AbortSignal } = {}) {
  const { data, response } = await api.GET('/templates', {
    params: { query: { type } },
    signal: options.signal,
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data.templates ?? [];
}

export async function createTemplateSnippet(body: TemplateMutationRequest): Promise<TemplatePayload> {
  const result = await api.POST('/templates', { body });
  return ensureOkWithPayload(result, 'テンプレートの作成に失敗しました');
}

export async function updateTemplateSnippet(
  id: number,
  body: TemplateMutationRequest
): Promise<TemplatePayload> {
  const result = await api.PUT('/templates/{id}', {
    params: { path: { id } },
    body,
  });
  return ensureOkWithPayload(result, 'テンプレートの更新に失敗しました');
}

export async function deleteTemplateSnippet(id: number): Promise<TemplatePayload> {
  const result = await api.DELETE('/templates/{id}', {
    params: { path: { id } },
  });
  return ensureOkWithPayload(result, 'テンプレートの削除に失敗しました');
}

export async function setDefaultTemplateSnippet(id: number): Promise<TemplatePayload> {
  const result = await api.PATCH('/templates/{id}/set-default', {
    params: { path: { id } },
  });
  return ensureOkWithPayload(result, 'デフォルト設定に失敗しました');
}

export function useTemplatesQuery(type: string) {
  return useQuery({
    queryKey: ['templates', type],
    queryFn: () => listTemplates(type),
  });
}
